// import parseTime, formatTime and set to filter
import i18n from '@/i18n'; // internationalization
export { parseTime, formatTime } from '@/utils';
import { formatDistanceToNow } from 'date-fns';

/**
 * Show plural label if time is plural number
 * @param {number} time
 * @param {string} label
 * @return {string}
 */
function pluralize(time, label) {
	if (time === 1) {
		return time + label;
	}
	return time + label + 's';
}

/**
 * @param {number} time
 */
export function timeAgo(time) {
	const between = Date.now() / 1000 - Number(time);
	if (between < 3600) {
		return pluralize(~~(between / 60), ' minute');
	} else if (between < 86400) {
		return pluralize(~~(between / 3600), ' hour');
	} else {
		return pluralize(~~(between / 86400), ' day');
	}
}

/**
 * Number formatting
 * like 10000 => 10k
 * @param {number} num
 * @param {number} digits
 */
export function numberFormatter(num, digits) {
	const si = [
		{ value: 1e18, symbol: 'E' },
		{ value: 1e15, symbol: 'P' },
		{ value: 1e12, symbol: 'T' },
		{ value: 1e9, symbol: 'G' },
		{ value: 1e6, symbol: 'M' },
		{ value: 1e3, symbol: 'k' }
	];
	for (let i = 0; i < si.length; i++) {
		if (num >= si[i].value) {
			return (num / si[i].value).toFixed(digits).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + si[i].symbol;
		}
	}
	return num.toString();
}

/**
 * 10000 => "10,000"
 * @param {number} num
 */
export function toThousandFilter(num) {
	return (+num || 0).toString().replace(/^-?\d+/g, m => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','));
}

/**
 * Upper case first char
 * @param {String} string
 */
export function uppercaseFirst(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * @param {String} string
 */
export function risoltoStatusFilter(string) {
	if (string === 'OPEN') {
		return 'warning';
	} else if (string === 'OVERDUE') {
		return 'danger';
	} else if (string === 'CLOSED' || string === 'PAID') {
		return 'success';
	} else {
		return 'info';
	}
}

/**
 * @param {String} string
 */
export function channelFilter(string) {
	if (string === 'MAIL') {
		return i18n.t('byPostalMail');
	} else if (string === 'PHONE') {
		return i18n.t('phoneCall');
	} else if (string === 'NONE') {
		return i18n.t('NONE');
	} else if (string === 'EMAIL') {
		return 'Email';
	} else {
		return string;
	}
}

export function risoltoBooleanFilter(value) {
	if (value === true) {
		return i18n.t('yes');
	} else if (value === false) {
		return i18n.t('no');
	}
}

export function invoiceStatusFilter(value) {
	value = value.toUpperCase();

	if (value === 'PAID') {
		return i18n.t('paid');
	} else if (value === 'OVERDUE') {
		return i18n.t('overdue');
	} else if (value === 'OPEN') {
		return i18n.t('open');
	} else if (value === 'CLOSED') {
		return i18n.t('closed');
	} else if (value === 'LOADED') {
		return i18n.t('loaded');
	} else if (value === 'READY_FOR_SUBMISSION') {
		return i18n.t('readyForSubmission');
	} else if (value === 'PARSED') {
		return i18n.t('parsed');
	} else if (value === 'FAILED') {
		return i18n.t('failed');
	} else if (value === 'IN_REVIEW') {
		return i18n.t('inReview');
	} else {
		return 'info';
	}
}

export function paymentPlanStatusTranslate(value) {
	if (value === 'DRAFT') {
		return i18n.t('draft');
	}
}

export function eventsTypeIconFilter(value) {
	if (value === 'OPEN') {
		return 'envelope-open-regular';
	} else if (value === 'CLICK') {
		return 'mouse-pointer-solid';
	} else if (value === 'DELIVERY') {
		return 'paper-plane-regular';
	} else if (value === 'COMPLAINT' || value === 'BOUNCE') {
		return 'exclamation-triangle-solid';
	} else if (value === 'PROCESSING') {
		return 'cogs-solid';
	} else if (value === 'TRANSIT') {
		return 'transit';
	} else if (value === 'DELIVERED') {
		return 'circle-check';
	} else {
		return '';
	}
}

export function timelineIconFilter(value) {
	if (value === 'EMAIL') {
		return 'at-solid';
	} else if (value === 'SMS') {
		return 'sms-solid';
	} else if (value === 'PHONE') {
		return 'phone-solid';
	} else if (value === 'MAIL') {
		return 'envelope-solid';
	} else if (value === 'PEPPOL') {
		return 'envelope-solid'; // TODO: PEPPOL: change icon, e.g. electronic receipt
	} else if (value === 'NOTE') {
		return 'documentation';
	} else if (value === 'INVOICE_DISPUTE') {
		return 'bug';
	} else {
		return '';
	}
}

export function currencyFilter(value) {
	if (value === 'EUR') {
		return '€';
	} else if (value === 'USD') {
		return '$';
	} else {
		return 'no currency available';
	}
}

export function risoltoNumberFormatter(value) {
	const formatted = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);
	// console.log(formatted);
	return formatted;
}

export function formatBigNumber(value, decimals = 2) {
	const formatted = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: decimals }).format(value);

	return formatted;
}

/**
 * Return in words how long ago a certain Date was
 */

export function fromNow(time) {
	return formatDistanceToNow(time, { addSuffix: true });
}

export function excerpt(val) {
	if (val.length > 300) {
		return val.substr(0, 300);
	} else {
		return val;
	}
}

export function templateLength(val) {
	if (val.length > 23) {
		return `${val.substr(0, 23)}...`;
	} else {
		return val;
	}
}

export function capitalize(s) {
	if (typeof s !== 'string') return '';
	return s.charAt(0).toUpperCase() + s.slice(1);
}
